import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle } from 'lucide-react';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  Field,
  FieldLabel,
  FieldError,
} from '../../components/ui';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export type ForgotPasswordFormProps = {
  title?: string;
  loginPath: string;
  onSubmit: (email: string) => Promise<void>;
};

export function ForgotPasswordForm({
  title = 'Reset your password',
  loginPath,
  onSubmit,
}: ForgotPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const handleFormSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      try {
        await onSubmit(data.email);
      } catch {
        // Always show generic success (no email enumeration)
      }
      setSubmittedEmail(data.email);
    } finally {
      setIsLoading(false);
    }
  };

  if (submittedEmail) {
    return (
      <Card className="shadow-2xl border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
        <CardContent className="pt-8 pb-6 space-y-4 text-center">
          <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400 mx-auto" aria-hidden />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            If an account exists for <span className="font-medium">{submittedEmail}</span>, we sent a
            password reset link. Check your inbox and spam folder.
          </p>
          <Link
            to={loginPath}
            className="inline-block text-sm font-medium text-primary-600 hover:text-primary-800 dark:text-primary-300"
          >
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-2xl border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
      <CardHeader className="text-center pb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Enter your email and we&apos;ll send you a reset link.
        </p>
      </CardHeader>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <CardContent className="space-y-4">
          <Field data-invalid={!!errors.email?.message}>
            <FieldLabel htmlFor="forgot-email">Email address</FieldLabel>
            <Input
              id="forgot-email"
              type="email"
              autoComplete="email"
              aria-invalid={!!errors.email?.message}
              className="dark:border-gray-500 dark:bg-gray-900/60"
              {...register('email')}
            />
            <FieldError>{errors.email?.message}</FieldError>
          </Field>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full btn-primary" isLoading={isLoading} disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send reset link'}
          </Button>
          <Link
            to={loginPath}
            className="text-center text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
          >
            Back to sign in
          </Link>
        </CardFooter>
      </form>
    </Card>
  );
}
